import React, { useState } from 'react';
import { X, Send, AlertCircle, CheckCircle } from 'lucide-react';
import { sendSunMedInvoice } from '../utils/api';

const SunMedInvoicePreview = ({ invoice, onClose, onSent }) => {
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const handleSend = async () => {
    try {
      setSending(true);
      const result = await sendSunMedInvoice(invoice.id);
      setSendResult({ success: true, data: result });
      
      // Wait 2 seconds to show success message, then close
      setTimeout(() => {
        onSent();
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Failed to send invoice:', error);
      setSendResult({
        success: false,
        error: error.response?.data?.detail || error.message
      });
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl p-8 max-w-4xl w-full my-8 shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 pb-4 border-b-2 border-gray-200">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Invoice Preview</h2>
            <p className="text-gray-600 text-sm">Review before sending</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
            disabled={sending}
          >
            <X className="w-8 h-8" />
          </button>
        </div>

        {/* Send Result Messages */}
        {sendResult && (
          <div className={`mb-6 p-4 rounded-lg border-2 ${sendResult.success ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'}`}>
            {sendResult.success ? (
              <div className="flex items-center space-x-2 text-green-700">
                <CheckCircle className="w-5 h-5" />
                <div>
                  <p className="font-semibold">Invoice sent successfully!</p>
                  <p className="text-sm">Sent to {sendResult.data?.recipient}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-2 text-red-700">
                <AlertCircle className="w-5 h-5" />
                <div>
                  <p className="font-semibold">Failed to send invoice</p>
                  <p className="text-sm">{sendResult.error}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Recipient Confirmation */}
        <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-500 rounded-lg">
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-900">This invoice will be sent to:</p>
              <p className="text-blue-700 text-lg font-bold mt-1">{invoice.billToEmail}</p>
            </div>
          </div>
        </div>

        {/* Invoice Preview (mimics email template) */}
        <div className="bg-gradient-to-br from-cyan-500 to-blue-500 text-white p-6 rounded-t-lg">
          <h1 className="text-3xl font-bold mb-1">INVOICE</h1>
          <p className="opacity-90">Medical Transport Services</p>
        </div>

        <div className="bg-gray-100 p-4 border-x border-gray-200">
          <div className="flex justify-between">
            <div>
              <p className="text-gray-600 text-sm">Invoice Number</p>
              <p className="text-xl font-bold text-gray-900">{invoice.invoiceNumber}</p>
            </div>
            <div className="text-right">
              <p className="text-gray-600 text-sm">Date Issued</p>
              <p className="text-lg font-semibold text-gray-900">{invoice.dateIssued}</p>
            </div>
          </div>
        </div>

        <div className="border border-gray-200 p-6 space-y-6">
          {/* From & Bill To */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="text-xs font-bold text-gray-500 uppercase mb-2">From</h3>
              <div className="bg-gray-50 p-4 rounded border border-gray-200">
                <p className="font-semibold text-gray-900">{invoice.fromName}</p>
                <p className="text-gray-600 text-sm">{invoice.fromAddress}</p>
                <p className="text-gray-600 text-sm">📞 {invoice.fromPhone}</p>
                <p className="text-gray-600 text-sm">📧 {invoice.fromEmail}</p>
                <p className="text-blue-600 text-sm">🌐 {invoice.fromWebsite}</p>
              </div>
            </div>

            <div>
              <h3 className="text-xs font-bold text-gray-500 uppercase mb-2">Bill To</h3>
              <div className="bg-gray-50 p-4 rounded border border-gray-200">
                <p className="font-semibold text-gray-900">{invoice.billToName}</p>
                <p className="text-gray-600 text-sm">{invoice.billToAddress}</p>
                <p className="text-gray-600 text-sm">📧 {invoice.billToEmail}</p>
              </div>
            </div>
          </div>

          {/* Patient Info */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
            <h3 className="font-semibold text-blue-900 mb-2">Patient Information</h3>
            <p className="text-blue-800 text-sm">
              <strong>Name:</strong> {invoice.patientName}
            </p>
            <p className="text-blue-800 text-sm">
              <strong>Address:</strong> {invoice.patientAddress}
            </p>
            <p className="text-blue-800 text-sm">
              <strong>Phone:</strong> {invoice.patientPhone}
            </p>
          </div>

          {/* Appointment Details */}
          <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
            <h3 className="font-semibold text-green-900 mb-2">Appointment Details</h3>
            <p className="text-green-800 text-sm">
              <strong>Date:</strong> {invoice.appointmentDate}
            </p>
            <p className="text-green-800 text-sm">
              <strong>Time:</strong> {invoice.appointmentTime}
            </p>
            <p className="text-green-800 text-sm">
              <strong>Destination:</strong> {invoice.appointmentDestination}
            </p>
          </div>

          {/* Charges */}
          <div>
            <h3 className="font-semibold text-gray-900 text-lg mb-3">Charges</h3>
            <table className="w-full border-collapse border border-gray-200 rounded overflow-hidden">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left p-3 text-gray-700 font-semibold border-b-2 border-gray-300">
                    Description
                  </th>
                  <th className="text-right p-3 text-gray-700 font-semibold border-b-2 border-gray-300">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoice.lineItems.map((item, index) => (
                  <tr key={index} className="border-b border-gray-200">
                    <td className="p-3">
                      <strong className="text-gray-900">{item.label}</strong>
                      <br />
                      <span className="text-gray-600 text-sm">{item.description}</span>
                    </td>
                    <td className="p-3 text-right font-semibold text-gray-900">
                      ${item.amount.toFixed(2)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-yellow-100">
                  <td className="p-4 font-bold text-lg text-yellow-900">TOTAL</td>
                  <td className="p-4 text-right font-bold text-2xl text-yellow-900">
                    ${invoice.total.toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Note */}
          <div className="bg-yellow-50 border border-yellow-400 p-4 rounded">
            <p className="text-yellow-900 text-sm italic">
              <strong>Note:</strong> Under similar conditions, the total may reach $125.00 depending on
              distance and wait time.
            </p>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="bg-gray-50 border border-gray-200 p-4 rounded">
              <h3 className="font-semibold text-gray-900 mb-2">Notes</h3>
              <p className="text-gray-700 text-sm whitespace-pre-wrap">{invoice.notes}</p>
            </div>
          )}

          {/* Footer */}
          <div className="text-center pt-4 border-t-2 border-gray-200">
            <p className="text-gray-600 text-sm mb-1">Thank you for your business!</p>
            <p className="text-gray-500 text-xs">
              Taxi-98 • Nassau, The Bahamas • www.taxi-98.com
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex justify-end gap-3 pt-6 border-t-2 border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-semibold"
            disabled={sending}
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || sendResult?.success}
            className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition font-semibold flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-5 h-5" />
            <span>{sending ? 'Sending...' : sendResult?.success ? 'Sent!' : 'Send Invoice'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SunMedInvoicePreview;
